/**
 * Helper functions to map API responses to GenericTable format
 */

// Shared date formatter — single formatting authority for the dashboard, so the
// Yield tab and Batch Genealogy can never disagree on the same field again.
import { formatDisplayDate } from "../../CommonMethods/CommonMethods";

// Placeholder for columns with no value / no backend mapping yet. Left blank
// (empty) per requirement — do NOT show "NA".
const NO_MAPPING_PLACEHOLDER = "";

// CQA value for the In-Process / Product Release test tables. The API sends a
// boolean `cqa` (true / false) which React would render as nothing, so map it
// to "Yes" / "No". Primary key is `cqa`; tolerate the casing / naming variants
// seen across API shapes (the older contract used `cqasYesNo`).
const pickCqaValue = (item) => {
  const candidates = [
    item.cqa,
    item.cqas,
    item.CQA,
    item.cqA,
    item.cqasYesNo,
    item.criticalQualityAttribute,
  ];
  const raw = candidates.find(
    (v) => v !== undefined && v !== null && String(v).trim() !== ""
  );
  if (raw === undefined || raw === null) return "";
  // Boolean (or boolean-like string) → Yes / No.
  if (raw === true) return "Yes";
  if (raw === false) return "No";
  const lower = String(raw).trim().toLowerCase();
  if (lower === "true" || lower === "yes") return "Yes";
  if (lower === "false" || lower === "no") return "No";
  return String(raw).trim();
};

// Boolean (or boolean-like) value → "Yes" / "No". Empty/unknown → "".
// React renders raw booleans as nothing, so fields like informedBMS (true/false)
// must be mapped to text.
const formatYesNo = (v) => {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (v === undefined || v === null || v === '') return '';
  const s = String(v).trim().toLowerCase();
  if (s === 'true' || s === 'yes') return 'Yes';
  if (s === 'false' || s === 'no') return 'No';
  return String(v).trim();
};

// In-Process / Product Release tests deliver the operation number inside
// `operationDescription`, combined as "<description>/<operation number>", e.g.
//   "Water content of rinsed Tetrahydrofuran by KF(BSQ-IP-05 Test 2)/8".
// Split on the LAST "/": left is the description, right is the operation number.
const splitOperationDescription = (raw) => {
  const text = typeof raw === "string" ? raw : "";
  const idx = text.lastIndexOf("/");
  if (idx === -1) return { description: text, operationNumber: "" };
  return {
    description: text.slice(0, idx).trim(),
    operationNumber: text.slice(idx + 1).trim(),
  };
};

// Pull the list array out of whichever response shape the backend used.
// `key` may be a single key or an array of candidate keys (first non-empty wins) —
// used where the contract renamed a response key but legacy shapes may still appear.
const extractList = (apiResponse, key) => {
  const keys = Array.isArray(key) ? key : [key];
  for (const k of keys) {
    const list =
      apiResponse?.[k] ||
      apiResponse?.data?.[k] ||
      apiResponse?.data?.data?.[k];
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
};

// Safe value: keeps 0 / false, only blanks out undefined / null.
const val = (v) => (v !== undefined && v !== null ? v : "");

// Operation Number: show only the value after the last "/" (e.g. "BSQ.../2" -> "2").
const formatOperationNumber = (value) => {
  if (typeof value === "string" && value.includes("/")) {
    return value.split("/").pop().trim();
  }
  return value;
};

// Quantity Variance (%) derived from target vs. actual, matching the app's
// convention (deviation relative to the reference/target quantity):
//   ((actual - target) / target) * 100
// e.g. target 30, actual 80 -> 166.67. Returns "" when either value is missing
// or the target is 0 (can't divide) so the column shows a blank placeholder.
const computeQuantityVariance = (target, actual) => {
  const t = Number(target);
  const a = Number(actual);
  if (!Number.isFinite(t) || !Number.isFinite(a) || t === 0) return "";
  return parseFloat((((a - t) / t) * 100).toFixed(2));
};

// Build a "stacked" value for combined columns whose header contains "/"
// (e.g. Status / Category / Type). Each part becomes its own line (joined with
// "\n" for GenericTable's StackedCell, which draws a divider between lines).
// Empty parts render as "-" so every slot stays visible and aligned.
const stackParts = (parts) =>
  parts
    .map((p) => (p !== undefined && p !== null && String(p).trim() !== "" ? String(p).trim() : "-"))
    .join("\n");

/**
 * Map Materials & Consumption API response to table format
 * Response structure: { status, message, materialAndConsumptionList: [...] }
 * Dynamically render all fields from response
 */
export const mapMaterialAndConsumptionToTable = (apiResponse) => {
  const data = extractList(apiResponse, "materialAndConsumptionList");

  if (data.length === 0) {
    return { columns: [], rows: [] };
  }

  // PD Feedback changes (per approved screenshot):
  //  - Procedure Number      -> "Operation Number"
  //  - CMO Material Batch No. -> "CMO Lot Number" (the lot number now comes from
  //    cmoMaterialBatchNumber; the old vendorBatchNumber column that carried
  //    that header is dropped below, so only one "CMO Lot Number" is shown)
  //  - CMO Material Code      -> "CMO Material ID"
  //  - Sponsor Material Code removed
  //  - Timestamp (From) / Timestamp (To) added as the two leading columns
  //    (no backend mapping yet — placeholder value).
  const columnFieldMap = {
    collabrixBatchID: "Batch ID",
    procedureNumber: "Operation Number",
    operationNumber: "Operation Number",
    materialDescription: "Material Description",
    cmoMaterialCode: "CMO Material ID",
    cmoMaterialBatchNumber: "CMO Lot Number",
    targetQuantity: "Target Quantity",
    actualQuantity: "Actual Quantity",
    uom: "UOM",
    quantityVariance: "Quantity Variance (%)",
    quantityVaraince: "Quantity Variance (%)",
    supplierInformation: "Supplier Information",
  };

  // Columns removed from the table, or handled explicitly outside the dynamic
  // build. Sponsor Material Code and the old vendorBatchNumber "CMO Lot Number"
  // are dropped per the feedback; the Timestamp / Batch ID keys are leading
  // columns; Quantity Variance / Supplier Information are appended explicitly as
  // trailing columns (so they render even when the backend doesn't send those
  // keys yet). Excluded here to avoid duplicates.
  const HIDDEN_MATERIAL_COLUMNS = [
    'sponsorMaterialCode',
    'vendorBatchNumber',
    'collabrixBatchID',
    'timestampFrom',
    'timestampTo',
    'quantityVariance',
    'quantityVaraince',
    'supplierInformation',
  ];

  const firstItem = data[0];

  // Build the dynamic columns from the response (keeps any extra backend
  // columns such as Quantity Variance / Supplier Information in place).
  const dynamicColumns = Object.keys(firstItem)
    .filter((key) => !HIDDEN_MATERIAL_COLUMNS.includes(key))
    .map((key) => ({
      id: key,
      name: columnFieldMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
      field: key,
    }));

  const columns = [
    { id: 'timestampFrom', name: 'Timestamp (From)', field: 'timestampFrom' },
    { id: 'timestampTo', name: 'Timestamp (To)', field: 'timestampTo' },
    ...dynamicColumns,
    { id: 'quantityVariance', name: 'Quantity Variance (%)', field: 'quantityVariance' },
    // Supplier Information is rendered as a disabled "Click To View" link
    // (feature not wired up to a backend yet).
    { id: 'supplierInformation', name: 'Supplier Information', field: 'supplierInformation', disabledLink: true },
  ];

  const rows = data.map((item, index) => {
    const row = {
      id: `${val(item.collabrixBatchID)}${val(item.procedureNumber || item.operationNumber)}${index}`,
    };
    Object.keys(item).forEach((key) => {
      row[key] = val(item[key]);
    });
    // Operation Number: show only the value after the last "/".
    if (row.procedureNumber) row.procedureNumber = formatOperationNumber(row.procedureNumber);
    if (row.operationNumber) row.operationNumber = formatOperationNumber(row.operationNumber);
    // Timestamp columns have no backend mapping yet -> placeholder when absent.
    if (!row.timestampFrom) row.timestampFrom = NO_MAPPING_PLACEHOLDER;
    if (!row.timestampTo) row.timestampTo = NO_MAPPING_PLACEHOLDER;
    // Quantity Variance (%) — always derived on the FE from Target vs. Actual
    // quantity (the backend does not send this value). computeQuantityVariance
    // already returns "" (NO_MAPPING_PLACEHOLDER) when it can't be computed, so a
    // genuine 0% result is preserved rather than blanked out.
    row.quantityVariance = computeQuantityVariance(item.targetQuantity, item.actualQuantity);
    return row;
  });

  return { columns, rows };
};

/**
 * Map CPP Details API response to table format
 * Response structure: { status, message, cppDetailsList: [...] }
 * Or nested: { status, data: { cppDetailsList: [...] } }
 */
export const mapCPPDetailsToTable = (apiResponse) => {
  // Handle different response structures
  // Structure 1: { status: true, cppDetailsList: [...] }
  // Structure 2: { status: true, data: { cppDetailsList: [...] } }
  // Structure 3: { data: { status: true, data: { cppDetailsList: [...] } } }
  let data = [];
  
  if (apiResponse?.cppDetailsList) {
    // Direct structure
    data = apiResponse.cppDetailsList;
  } else if (apiResponse?.data?.cppDetailsList) {
    // Nested in data
    data = apiResponse.data.cppDetailsList;
  } else if (apiResponse?.data?.data?.cppDetailsList) {
    // Double nested
    data = apiResponse.data.data.cppDetailsList;
  }
  
  // Debug log removed — it printed real row data (sample: data[0]) to the console.
  // console.log('[CPPMapper] Mapping CPP details data:', {
  //   dataCount: data.length,
  //   sample: data[0],
  //   responseStructure: {
  //     hasDirectList: !!apiResponse?.cppDetailsList,
  //     hasNestedList: !!apiResponse?.data?.cppDetailsList,
  //     hasDoubleNested: !!apiResponse?.data?.data?.cppDetailsList
  //   }
  // });
  
  // Define columns dynamically based on the actual response structure
  const columns = [
    { id: 'collabrixCPPID', name: "CPP ID", field: "collabrixCPPID" },
    { id: 'procedureNumber', name: "Procedure Number", field: "procedureNumber" },
    { id: 'parameter', name: "Parameter", field: "parameter" },
    { id: 'setPoint', name: "Set Point", field: "setPoint" },
    { id: 'lowerLimit', name: "Lower Limit", field: "lowerLimit" },
    { id: 'upperLimit', name: "Upper Limit", field: "upperLimit" },
    { id: 'range', name: "Range", field: "range" }, // Computed field
    { id: 'actualValue', name: "Actual Value", field: "actualValue" },
    { id: 'uom', name: "UOM", field: "uom" },
  ];
  
  // Map data rows with all fields from response
  const rows = data.map((item, index) => {
    // Format range as "LowerLimit - UpperLimit"
    const range = item.lowerLimit !== undefined && item.upperLimit !== undefined 
      ? `${item.lowerLimit} - ${item.upperLimit}` 
      : "";
    
    return {
      id: item.collabrixCPPID || `cpp-${index}`, // Unique key for table
      collabrixBatchID: item.collabrixBatchID || "",
      collabrixCPPID: item.collabrixCPPID || "",
      procedureNumber: item.procedureNumber || "",
      parameter: item.parameter || "",
      setPoint: item.setPoint !== undefined && item.setPoint !== null ? item.setPoint : "",
      lowerLimit: item.lowerLimit !== undefined && item.lowerLimit !== null ? item.lowerLimit : "",
      upperLimit: item.upperLimit !== undefined && item.upperLimit !== null ? item.upperLimit : "",
      range: range,
      actualValue: item.actualValue !== undefined && item.actualValue !== null ? item.actualValue : "",
      uom: item.uom || "",
    };
  });
  
  // console.log('[CPPMapper] Mapped CPP details:', { columnsCount: columns.length, rowsCount: rows.length });
  
  return { columns, rows };
};

/**
 * Convert tab name to array name dynamically
 * Examples: "Material & Consumption" -> "materialAndConsumptionList"
 *           "Batch Records" -> "batchRecordsList"
 *           "Quality Events" -> "qualityEventsList"
 */
const tabNameToArrayName = (tabName) => {
  // Handle special cases first
  const specialCases = {
    "Material & Consumption": "materialAndConsumptionList",
    "Materials & Consumption": "materialAndConsumptionList",
    "CPP Details": "cppDetailsList",
    "CPPs": "cppDetailsList",
    "In Process Test": "inProcessTestList",
    "In-Process Tests": "inProcessTestList",
    "Product Release Test": "productReleaseTestList",
    "Product Release Tests": "productReleaseTestList",
    "Deviations": "deviationsList",
    "Change Controls": "changeControlList",
    "CC": "changeControlList",
    "Planned Change": "changeControlList",
    "Planned Changes": "changeControlList",
    "Equipment": "equipmentList",
    // Backend spec quirk: fetchBatchYield returns rows under `equipmentList`.
    "Yield": "equipmentList",
    "Batch Yield": "equipmentList",
  };
  
  if (specialCases[tabName]) {
    return specialCases[tabName];
  }
  
  // Generic conversion: "Batch Records" -> "batchRecordsList"
  // Remove special characters, convert to camelCase, append "List"
  const cleaned = tabName
    .replace(/[&]/g, '') // Remove &
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();
  
  const words = cleaned.split(' ');
  const camelCase = words.map((word, index) => {
    if (index === 0) {
      return word.charAt(0).toLowerCase() + word.slice(1);
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join('');
  
  return `${camelCase}List`;
};

/**
 * Helper: Get array name from tab name - Fully dynamic
 * Works for any tab name by converting it to camelCase + "List"
 */
const getArrayNameFromTabName = (tabName) => {
  return tabNameToArrayName(tabName);
};

/**
 * Generic mapper function that dynamically maps any tab response
 */
const mapGenericTabData = (apiResponse, arrayName, tabName) => {
  // Handle different response structures
  let data = [];
  
  if (apiResponse?.[arrayName]) {
    // Direct structure: { status: true, [arrayName]: [...] }
    data = apiResponse[arrayName];
  } else if (apiResponse?.data?.[arrayName]) {
    // Nested: { status: true, data: { [arrayName]: [...] } }
    data = apiResponse.data[arrayName];
  } else if (apiResponse?.data?.data?.[arrayName]) {
    // Double nested: { data: { status: true, data: { [arrayName]: [...] } } }
    data = apiResponse.data.data[arrayName];
  }
  
  // Debug log removed — it printed real row data (sample: data[0]) to the console.
  // console.log(`[${tabName}Mapper] Mapping ${tabName} data:`, {
  //   dataCount: data.length,
  //   sample: data[0],
  //   arrayName,
  //   responseStructure: {
  //     hasDirectList: !!apiResponse?.[arrayName],
  //     hasNestedList: !!apiResponse?.data?.[arrayName],
  //     hasDoubleNested: !!apiResponse?.data?.data?.[arrayName]
  //   }
  // });
  
  if (data.length === 0) {
    return { columns: [], rows: [] };
  }
  
  // Helper function to convert camelCase to Title Case
  // Keeps acronyms together (no spaces between letters) and adds space before them
  const camelCaseToTitleCase = (str) => {
    if (!str) return '';
    
    // Define all common acronyms that should stay together
    // Order matters: longer acronyms first to avoid partial matches
    const acronyms = [
      'CPP', 'CQA', 'BMS', 'CMO', 'API', 'QC', 'ID', 'CC' // CC = Change Control
    ];
    
    // Create a map of acronyms to placeholders
    const acronymMap = {};
    const placeholderMap = {};
    acronyms.forEach((acronym, index) => {
      const placeholder = `__acronym${index}__`;
      acronymMap[acronym.toUpperCase()] = placeholder;
      placeholderMap[placeholder] = acronym;
    });
    
    // Replace acronyms with placeholders (case-insensitive, longer first)
    // Sort by length descending to match longer acronyms first
    const sortedAcronyms = [...acronyms].sort((a, b) => b.length - a.length);
    let temp = str;
    sortedAcronyms.forEach(acronym => {
      const regex = new RegExp(acronym, 'gi');
      temp = temp.replace(regex, acronymMap[acronym.toUpperCase()]);
    });
    
    // Convert camelCase to Title Case
    // This adds spaces before capital letters
    // But we need to handle placeholders specially - they should trigger space addition
    let result = temp.charAt(0).toUpperCase() + temp.slice(1);
    // Add space before capitals, but treat placeholders as if they start with a capital
    result = result.replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space between lowercase and uppercase
    result = result.replace(/([a-z])(__acronym)/g, '$1 $2'); // Add space before placeholder if after lowercase
    
    // Restore acronyms as uppercase (in reverse order to maintain case)
    Object.keys(placeholderMap).sort((a, b) => b.length - a.length).forEach(placeholder => {
      const acronym = placeholderMap[placeholder];
      const regex = new RegExp(placeholder, 'gi');
      result = result.replace(regex, acronym);
    });
    
    // Ensure space before and after acronyms
    // This handles cases like "ChangeControlID" -> "Change Control ID"
    // Also handles "collabrixCPP" -> "Collabrix CPP"
    // And "CCNumber" -> "CC Number" (space after CC)
    sortedAcronyms.forEach(acronym => {
      // Add space BEFORE acronym if preceded by a letter (not already spaced)
      // Match any letter (lowercase or uppercase) followed directly by acronym without space
      const beforeRegex = new RegExp(`([a-zA-Z])(${acronym})(?=\\s|$|,|\\.|[A-Z])`, 'gi');
      result = result.replace(beforeRegex, '$1 $2');
      
      // Add space AFTER acronym if followed by a capital letter (start of next word)
      // This handles "CCNumber" -> "CC Number"
      const afterRegex = new RegExp(`(${acronym})([A-Z])`, 'gi');
      result = result.replace(afterRegex, '$1 $2');
    });
    
    // Clean up: remove multiple spaces and trim
    result = result.replace(/\s{2,}/g, ' ').trim();
    
    return result;
  };

  // Dynamically generate columns from the first item's keys
  const firstItem = data[0];
  const columnFieldMap = {
    // Common fields
    collabrixBatchID: "Batch ID",
    procedureNumber: "Procedure Number",
    operationNumber: "Operation Number",
    uom: "UoM",
    // In-Process Test specific
    operationDescription: "Operation Description",
    methodName: "Method Name",
    methodNumber: "Method Number",
    qcReceiptDate: "QC Receipt Date",
    inwardNumber: "Test Inward Number",
    arNumber: "Analytical Report Number",
    specificationLimit: "Specification Limit",
    resultValue: "Result Value",
    result: "Result",
    cqa: "CQA",
    // Equipment specific
    equipment: "Equipment ID",
    equipmentType: "Equipment Type",
    equipmentTitle: "Equipment Title",
    location: "Location",
    // Yield / Batch Yield specific
    plannedProduct: "Planned Product Quantity",
    actualProduct: "Actual Product Quantity",
    yieldPercentage: "Yield (%)",
    batchManufacturingDate: "Batch Manufacturing Date",
    expiryDate: "Expiry Date",
    storageCondition: "Storage Condition",
    storageLocation: "Storage Location",
  };

  // Fields to exclude from tab tables (internal IDs)
  const excludedFields = ["collabrixInProcessTestID", "collabrixProductReleaseTestID", "sampleDescription", "collabrixBatchID"];

  // Create columns dynamically from all keys in the first item
  const columns = Object.keys(firstItem)
    .filter(key => !excludedFields.includes(key))
    .map(key => ({
      id: key,
      name: columnFieldMap[key] || camelCaseToTitleCase(key), // Auto-format key name with proper acronym handling
      field: key
    }));

  // Map data rows with all fields from response
  const rows = data.map((item, index) => {
    const row = {
      id: item.collabrixInProcessTestID || item.collabrixProductReleaseTestID || item.collabrixBatchID || `row-${index}`, // Unique key for table
    };

    // Add all fields from the item
    Object.keys(item).forEach(key => {
      if (excludedFields.includes(key)) return;
      let value = item[key] !== undefined && item[key] !== null ? item[key] : "";
      // Operation Number: show only the value after the last "/"
      if (key === "operationNumber" && typeof value === "string" && value.includes("/")) {
        value = value.split("/").pop().trim();
      }
      row[key] = value;
    });

    return row;
  });
  
  // console.log(`[${tabName}Mapper] Mapped ${tabName}:`, {
  //   columnsCount: columns.length,
  //   rowsCount: rows.length,
  //   columns: columns.map(c => c.name)
  // });

  return { columns, rows };
};

/**
 * Shared row builder for the In-Process / Product Release test tables. Both tabs
 * share the same fields; only the inward-number column label differs.
 * options.sampleDescriptionFromMethodName — Product Release only: the Sample
 * Description column shows methodName, falling back to sampleDescription.
 */
const buildTestRows = (data, options = {}) =>
  data.map((item, index) => {
    // Backend now sends operationNumber and operationDescription as separate,
    // ready-to-display fields — map them directly, no splitting / fallback.
    return {
      id:
        item.collabrixInProcessTestID ||
        item.collabrixProductReleaseTestID ||
        item.collabrixBatchID ||
        `test-${index}`,
      operationNumber: val(item.operationNumber),
      // Operation Description: show only the text before the "/" — drop the
      // trailing operation number (e.g. "…(BSQ-IP-05 Test 2)/8" -> "…(BSQ-IP-05 Test 2)").
      operationDescription: splitOperationDescription(val(item.operationDescription)).description,
      methodNumber: val(item.methodNumber),
      sampleDescription: options.sampleDescriptionFromMethodName
        ? val(item.methodName) || val(item.sampleDescription)
        : val(item.sampleDescription),
      qcReceiptDate: formatDisplayDate(val(item.qcReceiptDate)),
      testInwardNumber: val(item.testInwardNumber),
      arNumber: val(item.arNumber),
      specificationLimit: val(item.specificationLimit),
      resultValue: val(item.resultValue),
      uom: val(item.uom),
      result: val(item.result),
      cqa: pickCqaValue(item),
      // Analytical Report is rendered as a "Click To View" link (see the
      // column's docLink flag) — it doesn't need its own value, arNumber above
      // is what the click handler matches against in Document Repository.
      analyticalReport: "",
    };
  });

/**
 * Map In-Process Test API response to table format
 * Response structure: { status, message, inProcessTestList: [...] }
 * Columns per approved screenshot.
 */
export const mapInProcessTestToTable = (apiResponse) => {
  const data = extractList(apiResponse, "inProcessTestList");

  const columns = [
    { name: "Operation Number", field: "operationNumber" },
    { name: "Operation Description", field: "operationDescription" },
    { name: "Method Number", field: "methodNumber" },
    { name: "Sample Description", field: "sampleDescription" },
    { name: "QC Receipt Date", field: "qcReceiptDate" },
    { name: "Test Inward Number", field: "testInwardNumber" },
    { name: "AR Number", field: "arNumber" },
    { name: "Specification Limit", field: "specificationLimit" },
    { name: "Result Value", field: "resultValue" },
    { name: "UOM", field: "uom" },
    { name: "Result", field: "result" },
    { name: "CQAs", field: "cqa" },
    // Opens the matching LIMS/Analytical document from the Document Repository
    // (Analytical category), matched on this row's arNumber + the current batch —
    // see GenericTable's docLink handling and ProjectTabs' onDocLinkClick.
    { name: "Analytical Report", field: "analyticalReport", docLink: true },
  ];

  return { columns, rows: buildTestRows(data) };
};

/**
 * Map Product Release Test API response to table format
 * Response structure: { status, message, productReleaseTestList: [...] }
 * Same shape as In-Process Tests; inward-number column is "Test Inward No.".
 */
export const mapProductReleaseTestToTable = (apiResponse) => {
  const data = extractList(apiResponse, "productReleaseTestList");

  const columns = [
    //{ name: "Operation Number", field: "operationNumber" },
    { name: "Operation Description", field: "operationDescription" },
    { name: "Method Number", field: "methodNumber" },
    { name: "Sample Description", field: "sampleDescription" },
    { name: "QC Receipt Date", field: "qcReceiptDate" },
    { name: "Test Inward No.", field: "testInwardNumber" },
    { name: "AR Number", field: "arNumber" },
    { name: "Specification Limit", field: "specificationLimit" },
    { name: "Result Value", field: "resultValue" },
    { name: "UOM", field: "uom" },
    { name: "Result", field: "result" },
    { name: "CQAs", field: "cqa" },
    // Opens the matching LIMS/Analytical document from the Document Repository
    // (Analytical category), matched on this row's arNumber + the current batch —
    // see GenericTable's docLink handling and ProjectTabs' onDocLinkClick.
    { name: "Analytical Report", field: "analyticalReport", docLink: true },
  ];

  // Product Release: Sample Description column maps to methodName (fallback to
  // sampleDescription).
  return { columns, rows: buildTestRows(data, { sampleDescriptionFromMethodName: true }) };
};

/**
 * Map Deviations API response to table format
 * PD Feedback changes (per approved screenshot):
 *  - "Informed Sponsor" column added (backend field: informedSponsor / informedBMS).
 *  - Assessment / Final report columns moved ahead of Corrective / Preventive
 *    and relabelled (drop the "(Email)" suffix); show "NA" when no value.
 *  - "CMO Deviation Status / Category / Type" combined column left unchanged.
 *  - "Sponsor Deviation Status / Deviation ID" combined column added
 *    (backend fields bmsDeviationStatus / bmsDeviationID).
 */
export const mapDeviationsToTable = (apiResponse) => {
  // Contract response key is `deviationsList` (legacy `deviationList` tolerated).
  const data = extractList(apiResponse, ["deviationsList", "deviationList"]);

  const columns = [
    { name: "Deviation Number", field: "deviationNumber" },
    { name: "Reporting Date / Step", field: "reportingDate", stacked: true },
    { name: "Initial Deviation Description", field: "initialDeviationDescription" },
    // rawValue: show the backend timestamp exactly as-is (no date formatting).
    { name: "Deviation Assessment Report", field: "deviationAssessmentReportEmail", rawValue: true },
    { name: "Final Deviation Report", field: "finalDeviationReportEmail", rawValue: true },
    { name: "Corrective Action", field: "correctiveAction" },
    { name: "Preventive Action", field: "preventiveAction" },
    { name: "CMO Deviation Status / Category / Type", field: "cmoDeviationCombined", stacked: true },
    { name: "Informed Sponsor", field: "informedSponsor", centered: true },
    { name: "Sponsor Deviation Status / Deviation ID", field: "sponsorDeviationCombined", stacked: true },
  ];

  const rows = data.map(item => {
    // Combine CMO Deviation Status / Category / Type — one per line (stacked),
    // empty slots shown as "-".
    const cmoDeviationCombined = stackParts([
      item.cmoDeviationStatus,
      item.cmoDeviationCategory,
      item.cmoDeviationType,
    ]);

    // Combine Sponsor Deviation Status + Deviation ID (backend fields
    // bmsDeviationStatus / bmsDeviationID) — one per line (stacked).
    const sponsorDeviationCombined = stackParts([item.bmsDeviationStatus, item.bmsDeviationID]);

    // "Reporting Date / Step": date on the first line, Step (Operation Number)
    // on the second. No backend Operation Number for deviations yet -> "NA".
    const reportingDate = val(item.reportingDate) || NO_MAPPING_PLACEHOLDER;
    const step = formatOperationNumber(val(item.operationNumber) || val(item.procedureNumber)) || NO_MAPPING_PLACEHOLDER;

    return {
      deviationNumber: val(item.deviationNumber),
      reportingDate: `${reportingDate}\n${step}`,
      initialDeviationDescription: val(item.initialDeviationDescription),
      // Report columns map directly to the backend timestamp fields; "NA" when empty.
      deviationAssessmentReportEmail:
        val(item.deviationAssessmentReportTimestamp) || NO_MAPPING_PLACEHOLDER,
      finalDeviationReportEmail:
        val(item.finalDeviationReportTimestamp) || NO_MAPPING_PLACEHOLDER,
      correctiveAction: val(item.correctiveAction),
      preventiveAction: val(item.preventiveAction),
      cmoDeviationCombined,
      // "Informed Sponsor" — backend field is informedBMS (boolean): true → Yes,
      // false → No. (legacy informedSponsor tolerated as a fallback.)
      informedSponsor: formatYesNo(
        item.informedBMS != null ? item.informedBMS : item.informedSponsor
      ),
      sponsorDeviationCombined,
      // Individual deviation values, additive to the combined/stacked columns
      // above. Some campaign seeds configure these as SEPARATE columns ("CMO
      // Deviation Status/Category/Type", "Sponsor Deviation Status/ID",
      // "Reporting Date", "Step") rather than the combined ones.
      // getVisibleColumns' fallback resolves those seed names to these fields so
      // they show their own value instead of blank — the combined columns above
      // are unchanged.
      cmoDeviationStatus: val(item.cmoDeviationStatus),
      cmoDeviationCategory: val(item.cmoDeviationCategory),
      cmoDeviationType: val(item.cmoDeviationType),
      bmsDeviationStatus: val(item.bmsDeviationStatus),
      bmsDeviationID: val(item.bmsDeviationID),
      reportingDateValue: reportingDate,
      deviationStep: step,
    };
  });

  return { columns, rows };
};

/**
 * Map Change Controls (CC) API response to table format
 * PD Feedback changes (per approved screenshot):
 *  - "Change Control Type" -> "CC Type"
 *  - "Change Control Number" column removed
 *  - "Initial/Final Procedure Number" -> "Initial/Final Document Number"
 *  - "CC Closure Report" column added (no backend mapping yet)
 *  - "CMO CC Status / CC ID" combined column (mapping present)
 *  - "Sponsor CC Status / CC ID" combined column added (no backend mapping yet)
 *  - "Affected Batches" column removed
 */
export const mapChangeControlsToTable = (apiResponse) => {
  const data = extractList(apiResponse, "changeControlList");
  // Planned Change rows (from fetchPlannedChange) are appended after the
  // change-control rows into the same columns.
  const plannedData = extractList(apiResponse, ["plannedChangeList", "plannedChanges"]);

  const columns = [
    { name: "CC Type", field: "ccType" },
    { name: "Initial Document Number", field: "initialProcedureNumber" },
    { name: "Final Document Number", field: "finalProcedureNumber" },
    { name: "Change From", field: "changeFrom" },
    { name: "Change To", field: "changeTo" },
    { name: "Reasons For Change", field: "reasonForChange" },
    // rawValue: show the backend timestamp exactly as-is (no date formatting).
    { name: "CC Closure Report", field: "ccClosureReport", rawValue: true },
    { name: "CMO CC Status / CC ID", field: "cmoCCCombined", stacked: true },
    { name: "Sponsor CC Status / CC ID", field: "sponsorCCCombined", stacked: true },
  ];

  const rows = data.map(item => {
    // Combined "/"-header columns render one value per line (stacked), empty
    // slots shown as "-".
    const cmoCCCombined = stackParts([item.cmoCCStatus, item.cmoCCID]);
    const sponsorCCCombined = stackParts([item.sponsorCCStatus, item.sponsorCCID]);

    return {
      ccType: val(item.ccType) || "Stage",
      initialProcedureNumber: val(item.initialProcedureNumber),
      finalProcedureNumber: val(item.finalProcedureNumber),
      changeFrom: val(item.changeFrom),
      changeTo: val(item.changeTo),
      reasonForChange: val(item.reasonForChange),
      // CC Closure Report — contract field ccClosureReportEmailTimestamp; "NA" when absent.
      ccClosureReport: val(item.ccClosureReportEmailTimestamp) || NO_MAPPING_PLACEHOLDER,
      cmoCCCombined,
      sponsorCCCombined,
      // Individual CC status / ID values, additive to the combined columns
      // above. Some campaign seeds configure these as SEPARATE columns ("CMO CC
      // ID", "CMO CC Status", "Sponsor CC ID", "Sponsor CC Status") rather than
      // the combined "Status / CC ID" ones. getVisibleColumns' fallback resolves
      // those seed names to these fields so they show their own value instead of
      // rendering blank — the combined columns above are unchanged.
      cmoCCStatus: val(item.cmoCCStatus),
      cmoCCID: val(item.cmoCCID),
      sponsorCCStatus: val(item.sponsorCCStatus),
      sponsorCCID: val(item.sponsorCCID),
    };
  });

  // Planned Change rows mapped into the CC columns (shared fields align 1:1;
  // the planned-change number is shown alongside the CMO CC status, and the
  // PC closure report feeds the CC Closure Report column). Sponsor CC has no
  // planned-change equivalent → left blank ("-").
  const plannedRows = plannedData.map(item => {
    const cmoCCCombined = stackParts([item.cmoCCStatus, item.plannedChangeNumber]);
    const sponsorCCCombined = stackParts([]);

    return {
      ccType: val(item.ccType) || "Stage",
      initialProcedureNumber: val(item.initialProcedureNumber),
      finalProcedureNumber: val(item.finalProcedureNumber),
      changeFrom: val(item.changeFrom),
      changeTo: val(item.changeTo),
      reasonForChange: val(item.reasonForChange),
      ccClosureReport: val(item.pcClosureReportEmailTimestamp) || NO_MAPPING_PLACEHOLDER,
      cmoCCCombined,
      sponsorCCCombined,
      // Individual values (see the note on the change-control rows above) — a
      // planned change carries only the CMO status; sponsor side stays blank.
      cmoCCStatus: val(item.cmoCCStatus),
      cmoCCID: val(item.plannedChangeNumber),
      sponsorCCStatus: "",
      sponsorCCID: "",
    };
  });

  return { columns, rows: [...rows, ...plannedRows] };
};

/**
 * Map Planned Change API response to table format.
 * Contract: POST /fetchPlannedChange -> { changeControlList: [...] } with fields
 * collabrixPlannedChangeID, plannedChangeNumber, ccType, initialProcedureNumber,
 * finalProcedureNumber, changeFrom, changeTo, reasonForChange, cmoCCStatus,
 * pcClosureReportEmailTimestamp.
 */
export const mapPlannedChangeToTable = (apiResponse) => {
  const data = extractList(apiResponse, ["plannedChangeList", "changeControlList"]);

  const columns = [
    { name: "Planned Change Number", field: "plannedChangeNumber" },
    { name: "CC Type", field: "ccType" },
    { name: "Initial Document Number", field: "initialProcedureNumber" },
    { name: "Final Document Number", field: "finalProcedureNumber" },
    { name: "Change From", field: "changeFrom" },
    { name: "Change To", field: "changeTo" },
    { name: "Reasons For Change", field: "reasonForChange" },
    { name: "PC Closure Report", field: "pcClosureReport" },
    { name: "CMO CC Status", field: "cmoCCStatus" },
  ];

  const rows = data.map((item, index) => ({
    id: item.collabrixPlannedChangeID || `pc-${index}`,
    plannedChangeNumber: val(item.plannedChangeNumber),
    ccType: val(item.ccType) || "Stage",
    initialProcedureNumber: val(item.initialProcedureNumber),
    finalProcedureNumber: val(item.finalProcedureNumber),
    changeFrom: val(item.changeFrom),
    changeTo: val(item.changeTo),
    reasonForChange: val(item.reasonForChange),
    pcClosureReport: val(item.pcClosureReportEmailTimestamp) || NO_MAPPING_PLACEHOLDER,
    cmoCCStatus: val(item.cmoCCStatus),
  }));

  return { columns, rows };
};

/**
 * Map Equipment API response to table format
 * PD Feedback changes (per approved screenshot):
 *  - "Operation Number" leading column added (no backend mapping yet -> "NA").
 *  - "Equipment Title" -> "Description"
 *  - "Actual Start Time" / "Actual End Time" columns added
 *    (no backend mapping yet -> "NA").
 *  - "Request Form" column rendered as a disabled "Click To View" link.
 */
export const mapEquipmentToTable = (apiResponse) => {
  const data = extractList(apiResponse, "equipmentList");

  const columns = [
    { name: "Operation Number", field: "operationNumber" },
    { name: "Equipment ID", field: "equipment" },
    { name: "Location", field: "location" },
    { name: "Equipment Type", field: "equipmentType" },
    { name: "Description", field: "equipmentTitle" },
    { name: "Actual Start Time", field: "actualStartTime" },
    { name: "Actual End Time", field: "actualEndTime" },
    { name: "Request Form", field: "requestForm", disabledLink: true },
  ];

  const rows = data.map((item, index) => ({
    id: `${val(item.collabrixBatchID)}${val(item.equipment)}${index}`,
    // Operation Number has no backend mapping yet -> placeholder.
    operationNumber: formatOperationNumber(val(item.operationNumber) || val(item.procedureNumber)) || NO_MAPPING_PLACEHOLDER,
    // Equipment ID column maps to equipmentTitle (e.g. "DHAR04").
    // Falls back to equipment / collabrixEquipmentID if not present.
    equipment: val(item.equipmentTitle) || val(item.equipment) || val(item.collabrixEquipmentID),
    location: val(item.location),
    equipmentType: val(item.equipmentType),
    equipmentTitle: val(item.equipmentTitle),
    actualStartTime: val(item.actualStartTime) || NO_MAPPING_PLACEHOLDER,
    actualEndTime: val(item.actualEndTime) || NO_MAPPING_PLACEHOLDER,
    // Request Form is rendered as a disabled "Click To View" link
    // (feature not wired up to a backend yet).
    requestForm: "",
  }));

  return { columns, rows };
};

/**
 * Map Batch Yield API response to table format.
 * Response structure: { status, message, equipmentList: [...] }. This is a
 * backend spec quirk — fetchBatchYield returns its rows under `equipmentList`
 * (was `batchYieldList`); legacy `batchYieldList` / `yieldList` still tolerated.
 * Per approved screenshot, the Input Mass / Input MW / Output Mass / Output MW
 * columns are dropped (no backend mapping); every other column is kept and
 * mapped to its available field. Internal IDs (collabrixBatchID /
 * collabrixBatchYieldID) and invBatchNumber / retestDate are not shown.
 */
export const mapBatchYieldToTable = (apiResponse) => {
  const data = extractList(apiResponse, ["equipmentList", "batchYieldList", "yieldList"]);

  const columns = [
    { name: "Planned Product Quantity", field: "plannedProductQuantity" },
    { name: "Actual Product Quantity", field: "actualProductQuantity" },
    { name: "UOM", field: "uom" },
    { name: "Yield (%)", field: "yield" },
    { name: "Molar Yield", field: "molarYield" },
    { name: "Batch Manufacturing Date", field: "batchManufacturingDate" },
    { name: "Expiry Date", field: "expiryDate" },
    { name: "Storage Condition", field: "storageCondition" },
    { name: "Storage Location", field: "storageLocation" },
  ];

  const rows = data.map((item, index) => ({
    id: item.collabrixBatchYieldID || item.collabrixBatchID || `yield-${index}`,
    plannedProductQuantity: val(item.plannedProductQuantity),
    actualProductQuantity: val(item.actualProductQuantity),
    uom: val(item.uom),
    yield: val(item.yield),
    molarYield: val(item.molarYield),
    batchManufacturingDate: formatDisplayDate(val(item.batchManufacturingDate)),
    expiryDate: formatDisplayDate(val(item.expiryDate)),
    storageCondition: val(item.storageCondition),
    storageLocation: val(item.storageLocation),
  }));

  return { columns, rows };
};

/**
 * Get mapper function for a tab name
 * Uses dedicated mappers for specific tabs, falls back to generic mapper
 */
export const getTabMapper = (tabName) => {
  // Dedicated mappers for specific tabs (explicit column layouts per the
  // approved PD feedback screenshots). Any tab without a dedicated mapper
  // falls back to the generic key-based mapper below.
  const dedicatedMappers = {
    "Materials & Consumption": mapMaterialAndConsumptionToTable,
    "Material & Consumption": mapMaterialAndConsumptionToTable,
    "In-Process Tests": mapInProcessTestToTable,
    "In Process Test": mapInProcessTestToTable,
    "Product Release Tests": mapProductReleaseTestToTable,
    "Product Release Test": mapProductReleaseTestToTable,
    "Deviations": mapDeviationsToTable,
    "Change Controls": mapChangeControlsToTable,
    "CC": mapChangeControlsToTable,
    "Planned Change": mapPlannedChangeToTable,
    "Planned Changes": mapPlannedChangeToTable,
    "Equipment": mapEquipmentToTable,
    "Yield": mapBatchYieldToTable,
    "Batch Yield": mapBatchYieldToTable,
  };

  if (dedicatedMappers[tabName]) {
    return dedicatedMappers[tabName];
  }

  // Get the array name for this tab
  const arrayName = getArrayNameFromTabName(tabName);

  if (!arrayName) {
    console.warn(`[TabMapper] No array name found for tab: ${tabName}`);
    return null;
  }

  // Return a generic mapper function that uses the array name
  return (apiResponse) => {
    return mapGenericTabData(apiResponse, arrayName, tabName);
  };
};
